import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import classnames from "classnames";
import {
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  UncontrolledDropdown,
} from "reactstrap";
import { DateOnly } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import { toast } from "react-toastify";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import FinalizePayrollRunConfirmModal from "features/Payroll/components/FinalizePayrollRunConfirmModal";
import CancelPayrollRunConfirmModal from "features/Payroll/components/CancelPayrollRunConfirmModal";
import DeletePayrollRunConfirmModal from "features/Payroll/components/DeletePayrollRunConfirmModal";
import PayRunConfirmModal from "features/Payroll/components/PayRunConfirmModal";
import {
  canCancelPayrollRun,
  canPayPayrollRun,
  isPayrollRunDraft,
  payrollRunStatusMap,
} from "features/Payroll/constants/payroll.constants";
import { downloadPayrollRunCsv } from "features/Payroll/utils/payrollRunCsvExport";
import { usePayrollRunDetail } from "./PayrollRunDetailContext";
import OverviewTab from "./OverviewTab";
import EmployeesTab from "./EmployeesTab";

const Section = () => {
  const { can } = useAccess();
  const { run, lineItems, totals, refetch } = usePayrollRunDetail();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [payRunModalOpen, setPayRunModalOpen] = useState(false);

  const hasPositiveNetItem = lineItems.some(
    (item) => Number(item.netPay ?? item.netAmount ?? 0) > 0,
  );

  const activeTab = searchParams.get("tab") === "employees" ? "2" : "1";

  const setTab = (tab: "1" | "2") => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === "2") next.set("tab", "employees");
        else next.delete("tab");
        return next;
      },
      { replace: true },
    );
  };

  if (!run) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">
            This payroll run could not be loaded.
          </p>
        </Col>
      </Row>
    );
  }

  return (
    <React.Fragment>
      <FinalizePayrollRunConfirmModal
        run={run}
        isOpen={finalizeModalOpen}
        onClose={() => setFinalizeModalOpen(false)}
        onFinalized={() => void refetch()}
      />
      <CancelPayrollRunConfirmModal
        run={run}
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onCancelled={() => void refetch()}
      />
      <DeletePayrollRunConfirmModal
        run={run}
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDeleted={() => navigate("/finance/payroll")}
      />
      <PayRunConfirmModal
        run={run}
        hasPositiveNetItem={hasPositiveNetItem}
        isOpen={payRunModalOpen}
        onClose={() => setPayRunModalOpen(false)}
        onPaid={() => void refetch()}
      />

      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3 align-items-start">
                  <div className="col-md">
                    <h4 className="fw-bold">Run {run.code ?? `#${run.id}`}</h4>
                    <div className="hstack gap-3 flex-wrap">
                      {run.status && (
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Status:</span>
                          <StatusBadge
                            value={run.status}
                            map={payrollRunStatusMap}
                          />
                        </div>
                      )}
                      <div className="vr d-none d-sm-block" />
                      <div className="text-muted">
                        Period:{" "}
                        <span className="text-body fw-medium">
                          <DateOnly date={run.periodStart} />
                        </span>
                        {" – "}
                        <span className="text-body fw-medium">
                          <DateOnly date={run.periodEnd} />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-auto mt-3 mt-md-0">
                    <div className="hstack gap-2 flex-nowrap">
                      {can(Permissions.PAYROLL.READ) && (
                        <Button
                          type="button"
                          size="sm"
                          color="light"
                          className="btn-label border"
                          disabled={!lineItems.length}
                          title={lineItems.length ? "Download this run as a CSV file" : "Nothing to export yet"}
                          onClick={() => { downloadPayrollRunCsv(run); toast.success("Payroll export downloaded"); }}
                        >
                          <i className="ri-download-2-line label-icon align-middle fs-14" />
                          Export CSV
                        </Button>
                      )}
                      {can(Permissions.PAYROLL.FINALIZE) && isPayrollRunDraft(run.status) && (
                        <Button
                          type="button"
                          size="sm"
                          color="success"
                          className="btn-label"
                          onClick={() => setFinalizeModalOpen(true)}
                        >
                          <i className="ri-check-double-line label-icon align-middle fs-14" />
                          Finalize run
                        </Button>
                      )}
                      {can(Permissions.PAYROLL.DISBURSE) && canPayPayrollRun(run.status) && (
                        <Button
                          type="button"
                          size="sm"
                          color="success"
                          className="btn-label"
                          onClick={() => setPayRunModalOpen(true)}
                        >
                          <i className="ri-hand-coin-line label-icon align-middle fs-14" />
                          Pay run
                        </Button>
                      )}
                      {(can(Permissions.PAYROLL.CANCEL) && canCancelPayrollRun(run.status)) ||
                      (can(Permissions.PAYROLL.DELETE) && isPayrollRunDraft(run.status)) ? (
                        <UncontrolledDropdown>
                          <DropdownToggle
                            type="button"
                            className="btn btn-light btn-sm border"
                          >
                            <i className="ri-more-2-fill align-middle" />
                          </DropdownToggle>
                          <DropdownMenu end>
                            {can(Permissions.PAYROLL.CANCEL) && canCancelPayrollRun(run.status) ? (
                              <DropdownItem
                                className="text-danger"
                                onClick={() => setCancelModalOpen(true)}
                              >
                                <i className="ri-close-circle-line align-bottom me-2" />
                                Cancel run
                              </DropdownItem>
                            ) : null}
                            {can(Permissions.PAYROLL.DELETE) && isPayrollRunDraft(run.status) ? (
                              <DropdownItem
                                className="text-danger"
                                onClick={() => setDeleteModalOpen(true)}
                              >
                                <i className="ri-delete-bin-line align-bottom me-2" />
                                Delete run
                              </DropdownItem>
                            ) : null}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      ) : null}
                    </div>
                  </div>
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setTab("1");
                      }}
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "2" },
                        "text-body fw-semibold",
                      )}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setTab("2");
                      }}
                    >
                      Employees
                      {totals.employees > 0 && (
                        <span className="badge bg-primary-subtle text-primary ms-1">
                          {totals.employees}
                        </span>
                      )}
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
            <TabPane tabId="2">
              <EmployeesTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Section;
